// useTreeCollapse — manages collapsed/expanded state for the org tree.
//
// Strategy on first render (current user known):
//   1. Walk up from the current user's UPN to the root via `managerUpn`,
//      collecting the manager chain. Every node on that chain is OPEN.
//   2. BFS down from the current user, marking every node within
//      `openDepth` (default 3) hops as OPEN.
//   3. Everything else starts COLLAPSED.
//
// Fallback when `currentUserUpn` is null: open root + 2 levels down (so a
// brand-new visitor without an Entra row still sees a useful subtree
// instead of one node).
//
// State shape: a `Set<string>` of node ids whose subtrees are HIDDEN.
// `useTreeLayout` consumes the same identity reference to memoise the
// layout — `toggle`, `expandAll`, and `collapseAll` always return a fresh
// `Set` so React picks up the change.
//
// US5 (PRODOPSAI-471) will consume `expandAll` and `collapseAll` from the
// control bar; US3/US4 do not interact with this hook.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IOrgGraphNode, IOrgGraphResponse } from '@skystone/shared';

const DEFAULT_OPEN_DEPTH = 3;
const FALLBACK_OPEN_DEPTH = 1;

export interface UseTreeCollapseResult {
  /** Set of node ids whose subtrees are hidden. Stable reference per state. */
  collapsedIds: ReadonlySet<string>;
  /** Flip the collapsed state of one node id. */
  toggle: (id: string) => void;
  /** Expand every node. */
  expandAll: () => void;
  /** Collapse everything except the current user's manager chain. */
  collapseAll: () => void;
  /** Count of direct children currently hidden under `nodeId`, or 0. */
  hiddenChildCount: (nodeId: string) => number;
}

/** Build a `managerUpn -> [childIds]` index over the supplied nodes. */
function buildChildrenIndex(
  nodes: readonly IOrgGraphNode[],
): Map<string, string[]> {
  const idx = new Map<string, string[]>();
  for (const n of nodes) {
    if (n.managerUpn === null) continue;
    const list = idx.get(n.managerUpn) ?? [];
    list.push(n.id);
    idx.set(n.managerUpn, list);
  }
  return idx;
}

/** Build an `id -> node` lookup. */
function buildNodeIndex(
  nodes: readonly IOrgGraphNode[],
): Map<string, IOrgGraphNode> {
  const idx = new Map<string, IOrgGraphNode>();
  for (const n of nodes) idx.set(n.id, n);
  return idx;
}

/**
 * Walk up from `startId` via `managerUpn` until we reach a root or revisit
 * a node (defensive against data cycles). Returns the set of visited ids,
 * INCLUDING `startId` itself.
 */
function managerChain(
  startId: string,
  nodeIndex: ReadonlyMap<string, IOrgGraphNode>,
): Set<string> {
  const chain = new Set<string>();
  let cursor: string | null = startId;
  while (cursor !== null && !chain.has(cursor)) {
    chain.add(cursor);
    const current = nodeIndex.get(cursor);
    cursor = current?.managerUpn ?? null;
  }
  return chain;
}

/**
 * BFS down from `startId`, returning every id reachable within `maxDepth`
 * hops (inclusive). Cycle-safe.
 */
function descendantsWithinDepth(
  startId: string,
  childrenIndex: ReadonlyMap<string, string[]>,
  maxDepth: number,
): Set<string> {
  const reached = new Set<string>([startId]);
  let frontier: string[] = [startId];
  for (let depth = 0; depth < maxDepth; depth += 1) {
    const next: string[] = [];
    for (const id of frontier) {
      const children = childrenIndex.get(id) ?? [];
      for (const child of children) {
        if (reached.has(child)) continue;
        reached.add(child);
        next.push(child);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }
  return reached;
}

/** Real roots (managerUpn === null) in the node list. */
function rootIds(nodes: readonly IOrgGraphNode[]): string[] {
  return nodes.filter((n) => n.managerUpn === null).map((n) => n.id);
}

/** Total number of nodes in `rootId`'s subtree (inclusive). Cycle-safe. */
function subtreeSize(
  rootId: string,
  childrenIndex: ReadonlyMap<string, string[]>,
): number {
  const seen = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const child of childrenIndex.get(id) ?? []) stack.push(child);
  }
  return seen.size;
}

/**
 * The "primary" root = the root with the largest subtree. Real Entra exports
 * are scoped, so people whose manager sits outside the export surface as
 * extra top-level roots (PRODOPSAI-474). Rather than open every root on load
 * (a wide, expand-all-looking view), we open just the primary branch and
 * leave the others collapsed. Returns null when there are no roots.
 */
function largestRootId(
  nodes: readonly IOrgGraphNode[],
  childrenIndex: ReadonlyMap<string, string[]>,
): string | null {
  const roots = rootIds(nodes);
  if (roots.length === 0) return null;
  let best = roots[0];
  let bestSize = -1;
  for (const r of roots) {
    const size = subtreeSize(r, childrenIndex);
    if (size > bestSize) {
      bestSize = size;
      best = r;
    }
  }
  return best;
}

/**
 * Shared "collapse every non-leaf that isn't excluded" helper. Used by both
 * the initial-collapse computation and `collapseAll` so the inclusion rules
 * (skip leaves, skip excluded ids) live in exactly one place.
 */
function buildCollapsedFrom(
  nodes: readonly IOrgGraphNode[],
  excludeIds: ReadonlySet<string>,
  childrenIndex: ReadonlyMap<string, string[]>,
): Set<string> {
  const collapsed = new Set<string>();
  for (const n of nodes) {
    if (excludeIds.has(n.id)) continue;
    // Skip leaves — collapsing them would render a useless +0 badge.
    if ((childrenIndex.get(n.id) ?? []).length === 0) continue;
    collapsed.add(n.id);
  }
  return collapsed;
}

/**
 * Compute the set of node ids that should remain OPEN on first render.
 * Splitting this out keeps `computeInitialCollapsed` flat enough to stay
 * under SonarCloud's cognitive-complexity threshold (S3776).
 */
function computeInitialOpen(
  nodes: readonly IOrgGraphNode[],
  currentUserUpn: string | null,
  openDepth: number,
  nodeIndex: ReadonlyMap<string, IOrgGraphNode>,
  childrenIndex: ReadonlyMap<string, string[]>,
): Set<string> {
  const openIds = new Set<string>();
  if (currentUserUpn !== null && nodeIndex.has(currentUserUpn)) {
    for (const id of managerChain(currentUserUpn, nodeIndex)) openIds.add(id);
    for (const id of descendantsWithinDepth(currentUserUpn, childrenIndex, openDepth)) {
      openIds.add(id);
    }
  } else {
    // Fallback: open only the PRIMARY root (largest subtree) + a small
    // subtree below it. Opening every root would expand all the
    // outside-the-export-manager orphan-roots at once — see PRODOPSAI-474.
    const primary = largestRootId(nodes, childrenIndex);
    if (primary !== null) {
      for (const id of descendantsWithinDepth(primary, childrenIndex, FALLBACK_OPEN_DEPTH)) {
        openIds.add(id);
      }
    }
  }
  return openIds;
}

/**
 * Compute the initial collapsed set. Returns the set of ids whose subtrees
 * should be hidden on first render.
 */
function computeInitialCollapsed(
  nodes: readonly IOrgGraphNode[],
  currentUserUpn: string | null,
  openDepth: number,
): Set<string> {
  if (nodes.length === 0) return new Set();

  const nodeIndex = buildNodeIndex(nodes);
  const childrenIndex = buildChildrenIndex(nodes);
  const openIds = computeInitialOpen(
    nodes,
    currentUserUpn,
    openDepth,
    nodeIndex,
    childrenIndex,
  );
  return buildCollapsedFrom(nodes, openIds, childrenIndex);
}

/**
 * Collapse-state hook for the org tree.
 *
 * @param data - full org-graph response.
 * @param openDepth - how many BFS hops below the current user to keep open
 *   on first render. Defaults to 3.
 */
export function useTreeCollapse(
  data: IOrgGraphResponse,
  openDepth: number = DEFAULT_OPEN_DEPTH,
): UseTreeCollapseResult {
  // Indices over the data are stable across renders (recomputed only when
  // the nodes array identity changes).
  const childrenIndex = useMemo(() => buildChildrenIndex(data.nodes), [data.nodes]);
  const nodeIndex = useMemo(() => buildNodeIndex(data.nodes), [data.nodes]);

  // Lazy init so the initial-collapse algorithm runs once per mount, not
  // on every render. We deliberately don't re-seed when `data` changes
  // mid-session after seeding has run with real data — that would discard
  // any user expansions.
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() =>
    computeInitialCollapsed(data.nodes, data.currentUserUpn, openDepth),
  );

  // Track whether we've already seeded with non-empty data. If the hook
  // mounted with an empty payload (host loaded async, or used the hook
  // outside `<OrgTreeTab>`'s guard), the lazy init produced an empty set;
  // we must re-seed once real data arrives so the default-collapsed
  // behaviour actually applies. After the first non-empty seed, subsequent
  // data updates are ignored — user expansions are preserved.
  const hasSeededWithRealData = useRef(data.nodes.length > 0);

  useEffect(() => {
    if (hasSeededWithRealData.current) return;
    if (data.nodes.length === 0) return;
    hasSeededWithRealData.current = true;
    setCollapsedIds(computeInitialCollapsed(data.nodes, data.currentUserUpn, openDepth));
  }, [data.nodes, data.currentUserUpn, openDepth]);

  const toggle = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedIds(new Set());
  }, []);

  const collapseAll = useCallback(() => {
    const currentUserUpn = data.currentUserUpn;
    // Preserve the manager chain so the viewer doesn't lose their own
    // context when they hit "collapse all".
    const protectedIds = currentUserUpn !== null && nodeIndex.has(currentUserUpn)
      ? managerChain(currentUserUpn, nodeIndex)
      : new Set<string>();
    setCollapsedIds(buildCollapsedFrom(data.nodes, protectedIds, childrenIndex));
  }, [data.nodes, data.currentUserUpn, childrenIndex, nodeIndex]);

  const hiddenChildCount = useCallback(
    (nodeId: string): number => {
      if (!collapsedIds.has(nodeId)) return 0;
      return (childrenIndex.get(nodeId) ?? []).length;
    },
    [collapsedIds, childrenIndex],
  );

  return { collapsedIds, toggle, expandAll, collapseAll, hiddenChildCount };
}
